// MST
import { types, flow, cast } from 'mobx-state-tree'

import {
  ICertificationProcedureGetAllAttrs,
  ICertificationProcedureGetAttrs,
  ICertificationProcedureSaveAttrs,
} from 'interfaces/back-office/content-management/certification/procedure/certification-procedure-view.interface'
import {
  ICertificationProcedureApiGetAllResponse,
  ICertificationProcedureApiGetResponse,
  ICertificationProcedureApiPaginatedResponse,
  ICertificationProcedureApiSaveResponse,
} from 'interfaces/back-office/content-management/certification/procedure/certification-procedure-api.interface'
import { ICertificationProcedureDetail } from 'interfaces/back-office/content-management/certification/procedure'

// Extensions
import { withEnvironment } from 'models/extensions/with-environment'
import { withHelper_baseResponsePaginationTypes } from 'models/extensions/with-helper'

// Lodash
import omit from 'lodash/omit'

// Utils
import { dateUtils_formatDate } from 'utils/common/date.utils'
import { commonUtils_renderTableNumber } from 'utils/common/common.utils'

// Certification Procedure Props
const certificationProcedureProops = {
  number: types.maybeNull(types.number),
  id: types.maybeNull(types.number),
  imageURL: types.maybeNull(types.string),
  originalFileName: types.maybeNull(types.string),
  title: types.maybeNull(types.string),
  status: types.maybeNull(types.boolean),
  createdAt: types.maybeNull(types.string),
  updatedAt: types.maybeNull(types.string),
}

// Certification Procedure List Model
const CertificationProcedures = types
  .model('CertificationProcedures')
  .props(withHelper_baseResponsePaginationTypes(types.model('CertificationProcedureList').props(certificationProcedureProops)))

// Certification Procedure Detail Model
const CertificationProcedureDetail = types
  .model('CertificationProcedureDetail')
  .props(omit(certificationProcedureProops, ['number']))

const CertificationProcedureStoreModel = types
  .model('CertificationProcedure')
  .props({
    certificationProcedure_isLoading: types.optional(types.boolean, false),
    certificationProcedure_isSaveLoading: types.optional(types.boolean, false),
    certificationProcedure_certificationProcedures: types.optional(CertificationProcedures, {}),
    certificationProcedure_detail: types.optional(CertificationProcedureDetail, {}),
  })
  .actions(self => ({
    certificationProcedure_SET_LOADING: (payload: boolean): void => {
      self.certificationProcedure_isLoading = payload
    },
    certificationProcedure_SET_SAVE_LOADING: (payload: boolean): void => {
      self.certificationProcedure_isSaveLoading = payload
    },
    certificationProcedure_SET_CERTIFICATION_PROCEDURES: (payload: ICertificationProcedureApiPaginatedResponse): void => {
      const mapPayload = {
        ...payload,
        list: payload.list.map((list, index) => {
          return {
            ...list,
            number: commonUtils_renderTableNumber(index, payload),
            createdAt: dateUtils_formatDate(list.createdAt!, { tableFormat: true }),
            updatedAt: dateUtils_formatDate(list.updatedAt!, { tableFormat: true }),
          }
        }),
      }

      self.certificationProcedure_certificationProcedures = cast(mapPayload)
    },
    certificationProcedure_SET_CERTIFICATION_PROCEDURE: (payload: ICertificationProcedureDetail): void => {
      self.certificationProcedure_detail = cast(payload)
    },
    certificationProcedure_CLEAR_CERTIFICATION_PROCEDURES: (): void => {
      self.certificationProcedure_certificationProcedures = cast({})
    },
    certificationProcedure_CLEAR_CERTIFICATION_PROCEDURE: (): void => {
      self.certificationProcedure_detail = cast({})
    },
  }))
  .extend(withEnvironment)
  .actions(self => ({
    /**
     * @description Fetch certificationProcedures
     *
     * @param {ICertificationProcedureGetAllAttrs} payload
     *
     * @return {any} any
     */
    certificationProcedure_fetchCertificationProcedures: flow(function* (payload: ICertificationProcedureGetAllAttrs) {
      self.certificationProcedure_SET_LOADING(true)

      const response: ICertificationProcedureApiGetAllResponse =
        yield self.environment.certificationProcedureApi.certificationProcedure_getCertificationProcedures({
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      let modifyResponse = JSON.parse(JSON.stringify(response.res.data))
      delete modifyResponse.sortColumn
      delete modifyResponse.sortDirection
      if (response.ok) self.certificationProcedure_SET_CERTIFICATION_PROCEDURES(modifyResponse)

      self.certificationProcedure_SET_LOADING(false)
    }),

    /**
     * @description Fetch certification procedure
     *
     * @param {ICertificationProcedureGetAttrs} payload
     *
     * @return {any} any
     */
    certificationProcedure_fetchCertificationProcedure: flow(function* (payload: ICertificationProcedureGetAttrs) {
      self.certificationProcedure_SET_LOADING(true)

      const response: ICertificationProcedureApiGetResponse =
        yield self.environment.certificationProcedureApi.certificationProcedure_getCertificationProcedure({
          params: { id: payload.params.id },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        self.certificationProcedure_SET_CERTIFICATION_PROCEDURE(response.res.data)

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      if (!response.ok) {
        // Do when response error
        if (payload?.onError) payload.onError()
      }

      self.certificationProcedure_SET_LOADING(false)
    }),

    /**
     * @description Save certification procedure
     *
     * @param {ICertificationProcedureSaveAttrs} payload
     *
     * @return {any} any
     */
    certificationProcedure_saveCertificationProcedure: flow(function* (payload: ICertificationProcedureSaveAttrs) {
      self.certificationProcedure_SET_SAVE_LOADING(true)

      const response: ICertificationProcedureApiSaveResponse =
        yield self.environment.certificationProcedureApi.certificationProcedure_saveCertificationProcedure({
          params: { ...payload.params },
          body: { ...payload.body },
          requestConfig: { ...payload.requestConfig },
        })
      if (response.ok) {
        // Do when response success
        if (payload?.onSuccess) payload.onSuccess()
      }

      self.certificationProcedure_SET_SAVE_LOADING(false)
    }),
  }))

export const createCertificationProcedureStoreDefaultModel = () => types.optional(CertificationProcedureStoreModel, {})
